import { useTheme } from '@mui/material/styles';

import {
  getLegendPayloadKey,
  LegendInteraction,
} from './hooks/useLegendInteraction';

type LegendContentPayloadItem = {
  /** Label rendered next to the swatch. */
  value?: any;

  /** Swatch fill color. */
  color?: string;

  /** Series key, falls back to `value` via `getLegendPayloadKey` when absent. */
  dataKey?: any;

  /** Raw recharts payload entry, used as a fallback source for the key. */
  payload?: any;
};

type LegendContentProps = {
  /** Legend entries as supplied by recharts's `<Legend>` content render prop. */
  payload?: readonly LegendContentPayloadItem[];

  /** Direction items are laid out in, mirrors the `<Legend layout>` prop. */
  layout?: 'horizontal' | 'vertical';

  /** Hover/hidden state and container tracking from `useLegendInteraction`. */
  legend: LegendInteraction;

  /** Swatch shape. */
  iconType?: 'circle' | 'rect';
};

/**
 * Renders legend items with shared pointer tracking and per-series visibility.
 */
export function LegendContent({
  payload,
  layout = 'horizontal',
  legend,
  iconType = 'circle',
}: LegendContentProps) {
  const theme = useTheme();
  const fontFamily = theme.typography.fontFamily;
  const textColor = theme.palette.text.primary;

  if (!payload?.length) return null;

  return (
    <div
      {...legend.legendContainerProps}
      style={{
        display: 'flex',
        flexDirection: layout === 'vertical' ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: layout === 'vertical' ? 'flex-start' : 'center',
        flexWrap: 'wrap',
        gap: layout === 'vertical' ? 8 : 16,
        fontFamily,
      }}
    >
      {payload.map((entry, index) => {
        const key = getLegendPayloadKey(entry) || String(entry.value ?? index);
        const isHidden = legend.isSeriesHidden(key);
        return (
          <div
            key={key}
            {...legend.legendItemProps(key)}
            onClick={() => legend.onLegendClick(entry)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: isHidden ? 0.3 : 1,
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: iconType === 'circle' ? '50%' : 2,
                backgroundColor: entry.color,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span style={{ color: textColor, fontSize: 13 }}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}
